'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { apiPost } from '@/lib/api';
import PlayerSearchSelect from '@/components/player/PlayerSearchSelect';
import GameScoresInput, { type SingleGameScore, type MatchValidityInfo } from './GameScoresInput';
import { getCurrentUserPlayer } from '@/models/playerModel';
import { Input, Button } from '@/components/ui';

type Props = {
  /**
   * If provided, this player ID will be sent as player1Id to the API.
   * If not provided, the current authenticated user will be used as player1.
   */
  player1Id?: string;
  /**
   * If provided, shows the player selection for player 1 (admin mode).
   */
  allowPlayer1Selection?: boolean;

  /**
   * Label for player 1 selection (defaults to translation).
   */
  player1Label?: string;
  /**
   * Label for player 2 selection (defaults to translation).
   */
  player2Label?: string;
  /**
   * Called when match is successfully saved.
   */
  onSaveSuccess?: () => void;
  /**
   * Called when save fails.
   */
  onSaveError?: (error: string) => void;
};

export default function MatchRecordForm({
  player1Id,
  allowPlayer1Selection = false,
  player1Label,
  player2Label,
  onSaveSuccess,
  onSaveError,
}: Props) {
  const t = useTranslations('Record');
  const tCommon = useTranslations('Common');

  const now = useMemo(() => {
    const date = new Date();
    // Format as YYYY-MM-DDTHH:MM for datetime-local input
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }, []);
  const [playedAt, setPlayedAt] = useState<string>(now);
  const [selectedPlayer1Id, setSelectedPlayer1Id] = useState<string>(player1Id || '');
  const [_player1Name, setPlayer1Name] = useState<{
    first_name: string;
    last_name: string;
  } | null>(null);
  const [player2Id, setPlayer2Id] = useState<string>('');
  const [_player2Name, setPlayer2Name] = useState<{
    first_name: string;
    last_name: string;
  } | null>(null);
  const [_myGames, setMyGames] = useState<number>(3);
  const [_oppGames, setOppGames] = useState<number>(0);
  const [bestOf, setBestOf] = useState<1 | 3 | 5>(3);
  const [parTarget, setParTarget] = useState<number>(11);
  const [deuceRule, setDeuceRule] = useState<'win_by_2' | 'sudden_death'>('win_by_2');
  const [scores, setScores] = useState<SingleGameScore[]>(
    Array.from({ length: 5 }, () => ({ me: '', opp: '' })),
  );
  const [saving, setSaving] = useState<boolean>(false);
  const [saved, setSaved] = useState<boolean>(false);
  const [validity, setValidity] = useState<MatchValidityInfo | null>(null);

  const canSave = useMemo(() => {
    if (!player2Id) return false;
    if (allowPlayer1Selection && !selectedPlayer1Id) return false;
    if (!validity) return false;
    return validity.isComplete;
  }, [player2Id, selectedPlayer1Id, allowPlayer1Selection, validity]);

  const onSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      // Convert to server payload [p1, p2]
      const split = scores.slice(0, bestOf);
      const gameScores = split
        .filter((r) => typeof r.me === 'number' && typeof r.opp === 'number')
        .map((r) => [Number(r.me), Number(r.opp)] as [number, number]);

      // derive games won for quick UI feedback
      let a = 0;
      let b = 0;
      gameScores.forEach(([m, o]) => {
        if (m > o) a += 1;
        else if (o > m) b += 1;
      });
      setMyGames(a);
      setOppGames(b);

      // derive player1Id if not provided
      if (!player1Id) {
        const player = await getCurrentUserPlayer();
        if (!player) throw new Error('Player not found');
        player1Id = player.id;
      }

      // Prepare payload
      const payload: {
        player1Id: string;
        player2Id: string;
        playedAtISO: string;
        gameScores: [number, number][];
        eventType: string;
        matchFormat: {
          bestOf: 1 | 3 | 5;
          pointsPerGame: number;
          deuceRule: 'win_by_2' | 'sudden_death';
        };
      } = {
        player1Id,
        player2Id,
        playedAtISO: new Date(playedAt).toISOString(),
        gameScores,
        eventType: 'friendly',
        matchFormat: {
          bestOf,
          pointsPerGame: parTarget,
          deuceRule,
        },
      };

      // Include player1Id if provided (admin mode)
      if (allowPlayer1Selection && selectedPlayer1Id) {
        payload.player1Id = selectedPlayer1Id;
      }

      await apiPost('/api/user/record', payload);
      setSaved(true);
      onSaveSuccess?.();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save match';
      onSaveError?.(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-[600px] space-y-6">
      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave) void onSave();
        }}
      >
        <div className="space-y-4">
          {allowPlayer1Selection && (
            <div>
              <label className="mb-2 block text-sm font-medium">
                {player1Label || t('player1')}
              </label>
              <PlayerSearchSelect
                value={selectedPlayer1Id || null}
                onChange={(pid, selected) => {
                  setSelectedPlayer1Id(pid || '');
                  setPlayer1Name(
                    selected
                      ? {
                          first_name: selected.first_name || '',
                          last_name: selected.last_name || '',
                        }
                      : null,
                  );
                }}
                placeholder={player1Label || t('player1Placeholder')}
                allowClear
              />
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium">
              {player2Label || t('opponent')}
            </label>
            <PlayerSearchSelect
              value={player2Id || null}
              onChange={(pid, selected) => {
                setPlayer2Id(pid || '');
                setPlayer2Name(
                  selected
                    ? { first_name: selected.first_name || '', last_name: selected.last_name || '' }
                    : null,
                );
              }}
              placeholder={player2Label || t('opponentPlaceholder')}
              allowClear
            />
          </div>

          <Input
            label={t('playedAt')}
            type="datetime-local"
            value={playedAt}
            onChange={(e) => setPlayedAt(e.target.value)}
            max={now}
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-2 block text-sm font-medium">{t('matchFormat')}</label>
              <div className="space-y-2">
                <label className="text-foreground/70 flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="bestOf"
                    className="h-4 w-4"
                    checked={bestOf === 1}
                    onChange={() => setBestOf(1)}
                  />
                  {t('bestOf1')}
                </label>
                <label className="text-foreground/70 flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="bestOf"
                    className="h-4 w-4"
                    checked={bestOf === 3}
                    onChange={() => setBestOf(3)}
                  />
                  {t('bestOf3')}
                </label>
                <label className="text-foreground/70 flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="bestOf"
                    className="h-4 w-4"
                    checked={bestOf === 5}
                    onChange={() => setBestOf(5)}
                  />
                  {t('bestOf5')}
                </label>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">{t('pointsPerGame')}</label>
              <div className="space-y-2">
                <label className="text-foreground/70 flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="parTarget"
                    className="h-4 w-4"
                    checked={parTarget === 11}
                    onChange={() => setParTarget(11)}
                  />
                  {t('par11')}
                </label>
                <label className="text-foreground/70 flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="parTarget"
                    className="h-4 w-4"
                    checked={parTarget === 15}
                    onChange={() => setParTarget(15)}
                  />
                  {t('par15')}
                </label>
                <div className="text-foreground/70 flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="parTarget"
                    className="h-4 w-4"
                    checked={parTarget !== 11 && parTarget !== 15}
                    onChange={() => setParTarget(21)}
                  />
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={parTarget !== 11 && parTarget !== 15 ? parTarget : ''}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (val >= 1 && val <= 99) setParTarget(val);
                    }}
                    placeholder={t('customPoints')}
                    className="bg-background border-border w-16 rounded-md border px-2 py-1 text-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">{t('deuceRule')}</label>
              <div className="space-y-2">
                <label className="text-foreground/70 flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="deuceRule"
                    className="h-4 w-4"
                    checked={deuceRule === 'win_by_2'}
                    onChange={() => setDeuceRule('win_by_2')}
                  />
                  {t('deuceRuleWinBy2')}
                </label>
                <label className="text-foreground/70 flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="deuceRule"
                    className="h-4 w-4"
                    checked={deuceRule === 'sudden_death'}
                    onChange={() => setDeuceRule('sudden_death')}
                  />
                  {t('deuceRuleSuddenDeath')}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <GameScoresInput
            bestOf={bestOf}
            value={scores}
            onChange={setScores}
            parTarget={parTarget}
            deuceRule={deuceRule}
            onValidityChange={(_ok, info) => setValidity(info)}
            pointsLabel={t('totals')}
            gamesLabel={t('resultQuick')}
            completeLabel={t('complete')}
            incompleteLabel={t('incomplete')}
          />
        </div>

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={!canSave} loading={saving}>
            {saving ? t('saving') : tCommon('buttons.save')}
          </Button>
          {saved ? <div className="text-foreground/70 text-sm">{t('saved')}</div> : null}
        </div>
      </form>
    </div>
  );
}
